import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Landmark, Calculator, RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, EmptyInline } from "@/pages/shared";
import { KindBadge, ModeBadge, StatusBadge, fmt } from "./admin/interfaces/sesStatus";
import BoekhoudingPanel from "./BoekhoudingPanel";

function Kpi({ label, value, tone = "", testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className={`mt-1 font-mono text-2xl font-semibold leading-none ${tone}`}>{value}</p>
    </div>
  );
}

// Leesbaar statusoverzicht van de overheidsmeldingen, voor iedereen aan de receptie.
export default function InterfacesOverview() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overheid");
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/interfaces/ses/overview", { params: { lang } }); setD(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [lang]);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const TabBar = (
    <div className="flex gap-1 border-b border-border" data-testid="iface-subtabs">
      <button type="button" onClick={() => setTab("overheid")} data-testid="subtab-overheid" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "overheid" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}><Landmark className="inline h-4 w-4 mr-1" />{t("iface_tab_overheid")}</button>
      <button type="button" onClick={() => setTab("boekhouding")} data-testid="subtab-boekhouding" className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "boekhouding" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}><Calculator className="inline h-4 w-4 mr-1" />{t("iface_tab_boekhouding")}</button>
    </div>
  );
  if (tab === "boekhouding") return <div className="space-y-5">{TabBar}<BoekhoudingPanel /></div>;
  if (!d) return <div className="space-y-5">{TabBar}</div>;
  const healthy = d.enabled && d.auto_send && d.has_credentials && d.late === 0 && d.rejected === 0;
  const schedule = d.send_time ? `${t("ses_send_time")}: ${d.send_time}` : d.send_interval_min ? `${t("ses_send_interval")}: ${d.send_interval_min} min` : t("ses_interval_0");

  return (
    <div className="space-y-5" data-testid="interfaces-overview">
      {TabBar}
      <div className={`rounded-2xl border p-5 flex flex-wrap items-center justify-between gap-4 ${healthy ? "border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20"}`} data-testid="ovw-health">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${healthy ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}>
            {healthy ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
          </div>
          <div>
            <p className="font-display text-lg font-bold text-foreground flex items-center gap-2"><Landmark className="h-4 w-4" />{t("ses_title")} <ModeBadge mode={d.mode} t={t} /></p>
            <p className="text-sm text-muted-foreground" data-testid="ovw-health-text">
              {!d.enabled ? t("ovw_disabled") : !d.has_credentials ? t("ses_no_creds") : !d.auto_send ? t("ovw_manual") : healthy ? t("ovw_ok") : t("ovw_attention")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} data-testid="ovw-reload"><RefreshCw className="h-4 w-4" /></Button>
          {isAdmin && <Button asChild variant="outline" size="sm" data-testid="ovw-manage"><Link to="/admin/interfaces/ses"><Settings2 className="h-4 w-4 mr-1" />{t("ovw_manage")}</Link></Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label={t("iface_open")} value={d.open} tone={d.open ? "text-sky-700" : ""} testid="ovw-open" />
        <Kpi label={t("iface_pending")} value={d.pending} tone={d.pending ? "text-indigo-700" : ""} testid="ovw-pending" />
        <Kpi label={t("ses_st_accepted")} value={d.counts?.accepted || 0} tone="text-emerald-700" testid="ovw-accepted" />
        <Kpi label={t("iface_rejected")} value={d.rejected} tone={d.rejected ? "text-red-700" : ""} testid="ovw-rejected" />
        <Kpi label={t("ses_late")} value={d.late} tone={d.late ? "text-red-700" : ""} testid="ovw-late" />
      </div>

      <div className="grid gap-3 sm:grid-cols-3 text-sm">
        <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3" data-testid="ovw-schedule"><Clock className="h-4 w-4 text-muted-foreground" /><span>{schedule}</span></div>
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="ovw-next"><span className="text-muted-foreground">{t("ovw_next_send")}: </span><span className="font-mono">{d.next_send_at ? fmt(d.next_send_at) : "—"}</span></div>
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="ovw-last"><span className="text-muted-foreground">{t("iface_last_sent")}: </span><span className="font-mono">{d.last_sent_at ? fmt(d.last_sent_at) : t("iface_never")}</span></div>
      </div>

      <Panel title={t("ovw_recent")} testid="ovw-recent-panel">
        {d.recent.length === 0 ? <EmptyInline /> : (
          <ul className="divide-y divide-border/70">
            {d.recent.map((r) => (
              <li key={r.id} className="px-5 py-3 flex flex-wrap items-center gap-3" data-testid={`ovw-row-${r.id}`}>
                <KindBadge kind={r.kind} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground leading-snug">{r.description}</p>
                  <p className="text-[11px] text-muted-foreground font-mono">{r.filename} · {fmt(r.created_at)}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={r.status} t={t} />
                  {r.status === "ready" && r.send_after && <p className="mt-1 text-[11px] text-sky-700 font-semibold">{t("ses_will_send_at")} {fmt(r.send_after)}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
